<template>
  <div class="user-profile-page">
    <clipr-page-load-overlay
      :loading="isInitialPageLoading"
      :error="initialPageLoadError"
      :title="tt('common.loading', 'Cargando...')"
      :error-message="initialPageLoadError || tt('common.refreshPageHint', 'Something went wrong while loading the page. Please refresh the page.')"
      :refresh-label="tt('common.refresh', 'Refresh')"
      @refresh="reloadCurrentPage"
    />
    <div class="user-profile-shell clipr-theme twitch-theme">
      <header class="user-profile-header">
        <h1 class="user-profile-title">
          {{ $t("userProfile.streamerProfileTitle") }}
        </h1>
        <p class="user-profile-subtitle">
          {{ $t("userProfile.streamerProfileDescription") }}
        </p>
      </header>

      <section class="user-profile-card user-profile-card--connections">
        <div class="profile-preferences-row">
          <div class="preference-cell preference-cell--left">
            <div class="preference-row">
              <div class="preference-left">
                <span class="preference-icon" aria-hidden="true">
                  <i class="fa-solid fa-closed-captioning"></i>
                </span>
                <span class="preference-label">
                  {{ tt("userProfile.clipSubtitlesTitle", "Subtítulos") }}
                </span>
              </div>
              <div class="preference-right">
                <button
                  type="button"
                  class="subtitles-switch"
                  :class="{ 'is-on': subtitlesEnabled }"
                  :disabled="subtitlesSaving"
                  :aria-pressed="subtitlesEnabled ? 'true' : 'false'"
                  :aria-label="tt('userProfile.clipSubtitlesSwitchAria', 'Activar subtítulos en clips')"
                  @click="saveSubtitlesPreference(!subtitlesEnabled)"
                >
                  <span class="subtitles-switch-thumb" aria-hidden="true"></span>
                </button>
              </div>
            </div>
          </div>
          <div class="preference-cell preference-cell--right">
            <div class="preference-row">
              <div class="preference-left">
                <span class="preference-icon" aria-hidden="true">
                  <i class="fa-solid fa-palette"></i>
                </span>
                <span class="preference-label">
                  {{ tt("userProfile.subtitleStyleTitle", "Estilo") }}
                </span>
              </div>
              <div class="preference-right">
                <button
                  type="button"
                  class="subtitle-style-button"
                  @click="openSubtitleStyleModal"
                >
                  {{ tt("userProfile.subtitleStyleEdit", "Editar") }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="profile-language-row">
          <members-card
            :is-twitch-user="true"
            :show-font-settings="false"
            :show-timezone="false"
            minimal
          />
        </div>

        <div class="user-profile-divider" aria-hidden="true"></div>

        <social-connections-card
          compact
          unified
          @initial-load-complete="onSocialConnectionsInitialLoad"
        />
      </section>

      <subscription-cancellation-card />

      <router-link
        to="/affiliate-program"
        class="user-profile-card user-profile-affiliate-card"
      >
        <div class="user-profile-affiliate-icon">
          <i class="fa-solid fa-link" aria-hidden="true"></i>
        </div>
        <div class="user-profile-affiliate-copy">
          <h2>{{ tt("affiliateProgram.profileTitle", "Programa de Afiliados") }}</h2>
          <p>{{ tt("affiliateProgram.profileDescription", "Obtén tu enlace y gana 30% recurrente por cada suscripción referida.") }}</p>
        </div>
        <i class="ti-angle-right user-profile-affiliate-chevron" aria-hidden="true"></i>
      </router-link>

      <section
        v-if="showAdminProfileSettings"
        class="user-profile-card user-profile-card--admin"
      >
        <header class="admin-settings-header">
          <div>
            <h2 class="admin-settings-title">
              {{ $t("userProfile.adminBrandingTitle") }}
            </h2>
            <p class="admin-settings-intro">
              {{ $t("userProfile.adminBrandingHint") }}
            </p>
          </div>
          <span class="admin-only-badge">
            {{ $t("userProfile.adminOnlyBadge") }}
          </span>
        </header>

        <div v-if="showAdminCustomLogoCard" class="user-profile-admin-block">
          <personalization-card
            :is-twitch-user="true"
            variant="customLogo"
            logo-preview-mode="endcard"
          />
        </div>
        <div
          v-if="showAdminCustomLogoCard && showAdminWatermarkCard"
          class="user-profile-divider"
          aria-hidden="true"
        ></div>
        <div v-if="showAdminWatermarkCard" class="user-profile-admin-block">
          <admin-watermark-card :is-twitch-user="true" />
        </div>
      </section>
    </div>

    <div
      v-if="subtitleStyleModalOpen"
      class="subtitle-style-modal-backdrop"
      @click.self="closeSubtitleStyleModal"
    >
      <section
        class="subtitle-style-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="subtitle-style-modal-title"
      >
        <header class="subtitle-style-modal-header">
          <div>
            <h2 id="subtitle-style-modal-title">
              {{ tt("userProfile.subtitleStyleModalTitle", "Estilo de subtítulos") }}
            </h2>
            <p>{{ tt("userProfile.subtitleStyleModalHint", "Se aplicará a clips nuevos.") }}</p>
          </div>
          <button
            type="button"
            class="subtitle-style-close"
            :aria-label="tt('common.close', 'Cerrar')"
            @click="closeSubtitleStyleModal"
          >
            <i class="ti-close" aria-hidden="true"></i>
          </button>
        </header>

        <div class="subtitle-style-field">
          <span>{{ tt("userProfile.subtitleStyleFont", "Fuente") }}</span>
          <div class="subtitle-style-font-grid">
            <button
              v-for="font in subtitleFonts"
              :key="font"
              type="button"
              class="subtitle-style-font-option"
              :class="{ 'is-selected': subtitleStyleDraft.font === font }"
              :aria-pressed="subtitleStyleDraft.font === font"
              @click="subtitleStyleDraft.font = font"
            >
              <span class="subtitle-style-font-sample" :style="subtitleFontPreviewStyle(font)">
                CLIPR
              </span>
              <span class="subtitle-style-font-name">{{ font }}</span>
            </button>
          </div>
        </div>

        <label class="subtitle-style-field">
          <span>{{ tt("userProfile.subtitleStyleColor", "Color") }}</span>
          <div class="subtitle-style-color-control">
            <input
              v-model="subtitleStyleDraft.activeColor"
              type="color"
              class="subtitle-style-color-picker"
            />
            <span>{{ subtitleStyleDraft.activeColor }}</span>
          </div>
        </label>

        <div class="subtitle-style-preview" :style="subtitlePreviewStyle">
          <span class="subtitle-style-preview-active">CLIPR</span>
          <span>{{ tt("userProfile.subtitleStylePreviewSuffix", "es el mejor") }}</span>
        </div>

        <footer class="subtitle-style-modal-actions">
          <button type="button" class="subtitle-style-cancel" @click="closeSubtitleStyleModal">
            {{ tt("common.cancel", "Cancelar") }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="subtitleStyleSaving"
            @click="saveSubtitleStyle"
          >
            {{ subtitleStyleSaving ? tt("common.saving", "Guardando...") : tt("userProfile.savePreferences", "Guardar") }}
          </button>
        </footer>
      </section>
    </div>
  </div>
</template>
<script>
import MembersCard from "./UserProfile/MembersCard.vue";
import SocialConnectionsCard from "./UserProfile/SocialConnectionsCard.vue";
import PersonalizationCard from "./UserProfile/PersonalizationCard.vue";
import AdminWatermarkCard from "./UserProfile/AdminWatermarkCard.vue";
import SubscriptionCancellationCard from "./UserProfile/SubscriptionCancellationCard.vue";
import CliprPageLoadOverlay from "@/components/clipr/CliprPageLoadOverlay.vue";
import { useCurrentUserStore } from "@/stores/currentUser";
import { supabase } from "@/supabase";
import { updateSubtitlesEnabled } from "@/api/users";
import { getEffectiveUserId } from "@/utils/impersonation";

export default {
  components: {
    MembersCard,
    SocialConnectionsCard,
    PersonalizationCard,
    AdminWatermarkCard,
    SubscriptionCancellationCard,
    CliprPageLoadOverlay,
  },
  data() {
    return {
      isInitialPageLoading: true,
      initialPageLoadError: null,
      subtitlesEnabled: true,
      subtitlesSaving: false,
      subtitleStyleModalOpen: false,
      subtitleStyleSaving: false,
      subtitleStyle: {
        font: "DejaVu Sans",
        activeColor: "#FFF302",
      },
      subtitleStyleDraft: {
        font: "DejaVu Sans",
        activeColor: "#FFF302",
      },
    };
  },
  computed: {
    showAdminWatermarkCard() {
      const currentUserStore = useCurrentUserStore();
      return Boolean(currentUserStore.hasAdminAccess || currentUserStore.isImpersonating);
    },
    showAdminCustomLogoCard() {
      return this.showAdminWatermarkCard;
    },
    showAdminProfileSettings() {
      return this.showAdminCustomLogoCard || this.showAdminWatermarkCard;
    },
    subtitleFonts() {
      return [
        "DejaVu Sans",
        "DejaVu Sans Condensed",
        "Liberation Sans",
        "Anton",
        "Archivo Black",
        "Bebas Neue",
        "Bricolage Grotesque",
        "League Spartan",
        "Oswald",
      ];
    },
    subtitleFontFamilies() {
      return {
        "DejaVu Sans": "Clipr Subtitle DejaVu Sans",
        "DejaVu Sans Condensed": "Clipr Subtitle DejaVu Sans Condensed",
        "Liberation Sans": "Clipr Subtitle Liberation Sans",
        Anton: "Anton",
        "Archivo Black": "Archivo Black",
        "Bebas Neue": "Bebas Neue",
        "Bricolage Grotesque": "Bricolage Grotesque",
        "League Spartan": "League Spartan",
        Oswald: "Oswald",
      };
    },
    subtitleFontScales() {
      return {
        "DejaVu Sans": 1,
        "DejaVu Sans Condensed": 1,
        "Liberation Sans": 1,
        Anton: 1.04,
        "Archivo Black": 1,
        "Bebas Neue": 1.09,
        "Bricolage Grotesque": 1.04,
        "League Spartan": 1.04,
        Oswald: 1.09,
      };
    },
    subtitlePreviewStyle() {
      return {
        fontFamily: this.subtitleFontFamilies[this.subtitleStyleDraft.font],
        "--subtitle-preview-active-color": this.subtitleStyleDraft.activeColor,
        "--subtitle-preview-font-scale": this.subtitleFontScales[this.subtitleStyleDraft.font],
      };
    },
  },
  async mounted() {
    const currentUserStore = useCurrentUserStore();
    if (!currentUserStore.profile) {
      await currentUserStore.ensureSessionReady();
    }
    this.subtitlesEnabled = currentUserStore.profile?.subtitles_enabled !== false;
    if (
      currentUserStore.profile?.subtitle_font
      && currentUserStore.profile?.subtitle_active_color
    ) {
      this.subtitleStyle = {
        font: currentUserStore.profile.subtitle_font,
        activeColor: currentUserStore.profile.subtitle_active_color,
      };
    }
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    subtitleFontPreviewStyle(font) {
      return {
        fontFamily: this.subtitleFontFamilies[font],
        transform: `scale(${this.subtitleFontScales[font]})`,
      };
    },
    reloadCurrentPage() {
      if (typeof window !== "undefined" && window.location) {
        window.location.reload();
      }
    },
    onSocialConnectionsInitialLoad({ hasError = false } = {}) {
      this.initialPageLoadError = hasError
        ? this.tt("common.refreshPageHint", "Something went wrong while loading the page. Please refresh the page.")
        : null;
      this.isInitialPageLoading = false;
    },
    async saveSubtitlesPreference(nextValue) {
      if (this.subtitlesSaving) return;
      const currentUserStore = useCurrentUserStore();
      const { data: { session } } = await supabase.auth.getSession();
      const userId = getEffectiveUserId(session?.user?.id);
      if (!session || !userId) {
        this.$notify({
          message: this.tt("userProfile.sessionError", "Error: Sesión no encontrada. Por favor, inicia sesión de nuevo."),
          type: "danger",
        });
        return;
      }

      this.subtitlesSaving = true;
      try {
        const payload = await updateSubtitlesEnabled(session, userId, nextValue);
        this.subtitlesEnabled = payload.subtitles_enabled;
        if (currentUserStore.profile) {
          currentUserStore.profile.subtitles_enabled = payload.subtitles_enabled;
        }
      } catch (error) {
        this.$notify({
          message: error?.message || this.tt("common.genericError", "Ocurrió un error"),
          type: "danger",
        });
      } finally {
        this.subtitlesSaving = false;
      }
    },
    async openSubtitleStyleModal() {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = getEffectiveUserId(session?.user?.id);
      if (!session || !userId) {
        this.$notify({
          message: this.tt("userProfile.sessionError", "Error: Sesión no encontrada. Por favor, inicia sesión de nuevo."),
          type: "danger",
        });
        return;
      }
      const { data, error } = await supabase
        .from("user_info")
        .select("subtitle_font, subtitle_active_color")
        .eq("user_id", userId)
        .single();
      if (error) {
        this.$notify({
          message: error.message || this.tt("common.genericError", "Ocurrió un error"),
          type: "danger",
        });
        return;
      }
      this.subtitleStyle = {
        font: data.subtitle_font,
        activeColor: data.subtitle_active_color,
      };
      const currentUserStore = useCurrentUserStore();
      if (currentUserStore.profile) {
        currentUserStore.profile.subtitle_font = data.subtitle_font;
        currentUserStore.profile.subtitle_active_color = data.subtitle_active_color;
      }
      this.subtitleStyleDraft = { ...this.subtitleStyle };
      this.subtitleStyleModalOpen = true;
    },
    closeSubtitleStyleModal() {
      if (this.subtitleStyleSaving) return;
      this.subtitleStyleModalOpen = false;
    },
    async saveSubtitleStyle() {
      if (this.subtitleStyleSaving) return;
      const currentUserStore = useCurrentUserStore();
      const { data: { session } } = await supabase.auth.getSession();
      const userId = getEffectiveUserId(session?.user?.id);
      if (!session || !userId) {
        this.$notify({
          message: this.tt("userProfile.sessionError", "Error: Sesión no encontrada. Por favor, inicia sesión de nuevo."),
          type: "danger",
        });
        return;
      }

      this.subtitleStyleSaving = true;
      try {
        const { data: payload, error } = await supabase
          .from("user_info")
          .update({
            subtitle_font: this.subtitleStyleDraft.font,
            subtitle_active_color: this.subtitleStyleDraft.activeColor.toUpperCase(),
          })
          .eq("user_id", userId)
          .select("subtitle_font, subtitle_active_color")
          .single();
        if (error) throw error;
        this.subtitleStyle = {
          font: payload.subtitle_font,
          activeColor: payload.subtitle_active_color,
        };
        if (currentUserStore.profile) {
          currentUserStore.profile.subtitle_font = payload.subtitle_font;
          currentUserStore.profile.subtitle_active_color = payload.subtitle_active_color;
        }
        this.subtitleStyleModalOpen = false;
      } catch (error) {
        this.$notify({
          message: error?.message || this.tt("common.genericError", "Ocurrió un error"),
          type: "danger",
        });
      } finally {
        this.subtitleStyleSaving = false;
      }
    },
  },
};
</script>
<style scoped>
.user-profile-page {
  position: relative;
  min-height: 100vh;
  --page-overlay-offset-y: 50px;
  --page-overlay-offset-x: 45px;
}

.user-profile-shell {
  width: min(660px, 100%);
  margin: 0 auto;
  padding: 0.25rem 0 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

/* ===== Page header ===== */

.user-profile-header {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0 0.15rem;
}

.user-profile-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 650;
  line-height: 1.3;
  letter-spacing: -0.01em;
  color: var(--clipr-text-primary);
}

.user-profile-subtitle {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--clipr-text-secondary);
}

/* ===== Cards ===== */

.user-profile-card {
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background:
    linear-gradient(180deg, rgba(168, 85, 247, 0.055) 0%, rgba(168, 85, 247, 0) 120px),
    rgba(13, 13, 21, 0.85);
  backdrop-filter: blur(14px);
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.05) inset,
    0 16px 40px rgba(0, 0, 0, 0.22);
}

.user-profile-card--connections {
  padding-bottom: 0.3rem;
}

.profile-preferences-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.preference-cell {
  padding: 0.7rem 1.25rem;
  min-width: 0;
}

.preference-cell--left {
  border-right: 1px solid rgba(148, 163, 184, 0.09);
}

.preference-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 44px;
}

.preference-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
}

.preference-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.preference-icon {
  display: grid;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  place-items: center;
  border: 1px solid rgba(192, 132, 252, 0.22);
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(217, 70, 239, 0.1));
  color: #d8b4fe;
  font-size: 0.82rem;
}

.preference-label {
  color: var(--clipr-text-primary);
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
}

.subtitles-switch {
  position: relative;
  width: 42px;
  height: 24px;
  flex: 0 0 42px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  cursor: pointer;
  transition: background 0.2s ease;
}

.subtitles-switch.is-on {
  background: var(--clipr-purple, #a855f7);
}

.subtitles-switch:disabled {
  opacity: 0.55;
  cursor: wait;
}

.subtitles-switch-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s ease;
}

.subtitles-switch.is-on .subtitles-switch-thumb {
  transform: translateX(18px);
}

.subtitle-style-button,
.subtitle-style-cancel {
  border: 1px solid rgba(192, 132, 252, 0.32);
  border-radius: 9px;
  background: rgba(168, 85, 247, 0.11);
  color: #e9d5ff;
  padding: 0.38rem 0.68rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}

.subtitle-style-button:hover,
.subtitle-style-cancel:hover {
  border-color: rgba(192, 132, 252, 0.6);
  background: rgba(168, 85, 247, 0.2);
}

.profile-language-row {
  padding: 0.7rem 1.25rem;
  border-top: 1px solid rgba(148, 163, 184, 0.09);
}

.user-profile-affiliate-card {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1rem 1.15rem;
  color: var(--clipr-text-primary);
  text-decoration: none;
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.user-profile-affiliate-card:hover {
  color: var(--clipr-text-primary);
  text-decoration: none;
  border-color: rgba(192, 132, 252, 0.35);
  transform: translateY(-1px);
}

.user-profile-affiliate-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 13px;
  display: grid;
  place-items: center;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #d946ef);
}

.user-profile-affiliate-copy {
  min-width: 0;
  flex: 1;
}

.user-profile-affiliate-copy h2 {
  margin: 0 0 0.15rem;
  font-size: 0.92rem;
  font-weight: 650;
}

.user-profile-affiliate-copy p {
  margin: 0;
  color: var(--clipr-text-secondary);
  font-size: 0.76rem;
  line-height: 1.45;
}

.user-profile-affiliate-chevron {
  color: var(--clipr-text-tertiary);
}

.user-profile-divider {
  height: 1px;
  margin: 0 1.25rem;
  background: rgba(148, 163, 184, 0.09);
}

.user-profile-admin-block {
  min-width: 0;
}

/* ===== Language row (MembersCard minimal) ===== */

.user-profile-shell :deep(.members-settings-card) {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.profile-language-row :deep(.members-card-wrapper) {
  width: 100%;
}

.profile-language-row :deep(.members-settings-card-body--minimal) {
  padding: 0;
}

.profile-language-row :deep(.profile-language-row--minimal) {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 44px;
}

.profile-language-row :deep(.profile-language-identity) {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.profile-language-row :deep(.profile-language-icon) {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  font-size: 14px;
}

.profile-language-row :deep(.profile-language-title) {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--clipr-text-primary);
}

.profile-language-row :deep(.profile-language-row--minimal .profile-language-controls) {
  flex-shrink: 0;
  width: auto;
  min-width: 0;
  max-width: none;
  margin-left: 0;
}

.profile-language-row :deep(.profile-language-select) {
  border-radius: 9px;
  min-height: 34px;
  width: 7rem;
  padding: 0.3rem 1.75rem 0.3rem 0.65rem;
  font-size: 0.8125rem;
  font-weight: 500;
}

/* ===== Shared form theming ===== */

.user-profile-shell :deep(.form-control),
.user-profile-shell :deep(.border-input) {
  background-color: var(--clipr-bg-elevated) !important;
  border: 1px solid var(--clipr-border-color) !important;
  color: var(--clipr-text-primary) !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.user-profile-shell :deep(select.form-control),
.user-profile-shell :deep(select.border-input) {
  background-color: var(--clipr-bg-elevated) !important;
  -webkit-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") !important;
  background-repeat: no-repeat !important;
  background-position: right 0.65rem center !important;
  background-size: 12px !important;
}

.user-profile-shell :deep(select.form-control option),
.user-profile-shell :deep(select.border-input option) {
  background-color: var(--clipr-bg-elevated);
  color: var(--clipr-text-primary);
}

.user-profile-shell :deep(.form-control:focus),
.user-profile-shell :deep(.border-input:focus),
.user-profile-shell :deep(select.form-control:focus),
.user-profile-shell :deep(select.border-input:focus) {
  border-color: rgba(168, 85, 247, 0.5) !important;
  box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.14) !important;
  outline: none;
}

.user-profile-shell :deep(select.form-control:disabled),
.user-profile-shell :deep(select.border-input:disabled) {
  opacity: 0.55;
  cursor: not-allowed;
}

.user-profile-shell :deep(.facebook-page-select-icon) {
  width: 18px;
  height: 18px;
}

.user-profile-shell :deep(.facebook-page-id-select--solo) {
  width: 100%;
  min-height: 26px;
  padding: 0.2rem 1.35rem 0.2rem 0.45rem;
  font-size: 0.66rem;
  border-radius: 7px;
}

.user-profile-shell :deep(.btn-primary) {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  box-shadow: none;
}

.user-profile-shell :deep(.btn-primary:hover) {
  background: linear-gradient(135deg, #a855f7 0%, #d946ef 100%) !important;
  box-shadow: 0 4px 12px rgba(168, 85, 247, 0.25) !important;
  transform: none;
}

/* ===== Admin section ===== */

.admin-settings-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.05rem 1.25rem 0.2rem;
}

.admin-settings-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 650;
  line-height: 1.3;
  color: var(--clipr-text-primary);
}

.admin-settings-intro {
  margin: 0.3rem 0 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: var(--clipr-text-secondary);
}

.admin-only-badge {
  flex: 0 0 auto;
  padding: 0.22rem 0.6rem;
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 999px;
  background: rgba(168, 85, 247, 0.1);
  color: #d8b4fe;
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  line-height: 1.3;
}

.user-profile-card--admin :deep(.members-settings-card-header) {
  padding: 0.85rem 1.25rem 0;
}

.user-profile-card--admin :deep(.members-settings-card-title) {
  font-size: 0.9rem;
  font-weight: 600;
}

.user-profile-card--admin :deep(.members-settings-card-intro) {
  margin-top: 0.25rem;
  font-size: 0.78rem;
  line-height: 1.45;
}

.user-profile-card--admin :deep(.members-settings-card-body) {
  padding: 0.8rem 1.25rem 1.1rem;
}

.user-profile-card--admin :deep(.label-uppercase),
.user-profile-card--admin :deep(.control-label) {
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.78rem;
  font-weight: 500;
}

.user-profile-card--admin :deep(.btn-primary) {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  box-shadow: none;
}

.user-profile-card--admin :deep(.custom-logo-preview-stage--endcard) {
  min-height: 240px;
}

.user-profile-card--admin :deep(.endcard-preview-frame) {
  width: min(172px, 54vw);
  border-radius: 14px;
  padding: 14px 12px 16px;
}

.user-profile-card--admin :deep(.admin-watermark-preview-stage) {
  min-height: 140px;
}

.subtitle-style-modal-backdrop {
  position: fixed;
  z-index: 1200;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(3, 3, 8, 0.72);
}

.subtitle-style-modal {
  width: min(430px, 100%);
  border: 1px solid rgba(192, 132, 252, 0.25);
  border-radius: 16px;
  background: #15121f;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
  padding: 1.2rem;
}

.subtitle-style-modal-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.15rem;
}

.subtitle-style-modal-header h2 {
  margin: 0;
  color: var(--clipr-text-primary);
  font-size: 1rem;
  font-weight: 650;
}

.subtitle-style-modal-header p {
  margin: 0.28rem 0 0;
  color: var(--clipr-text-secondary);
  font-size: 0.78rem;
}

.subtitle-style-close {
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--clipr-text-secondary);
  cursor: pointer;
}

.subtitle-style-close:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--clipr-text-primary);
}

.subtitle-style-field {
  display: grid;
  gap: 0.4rem;
  margin-bottom: 0.95rem;
  color: var(--clipr-text-secondary);
  font-size: 0.76rem;
  font-weight: 600;
}

.subtitle-style-font-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.55rem;
}

.subtitle-style-font-option {
  display: grid;
  gap: 0.38rem;
  min-width: 0;
  padding: 0.5rem;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 10px;
  background: #211b2e;
  color: #f8fafc;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
}

.subtitle-style-font-option:hover {
  border-color: rgba(192, 132, 252, 0.58);
  background: #2a213a;
  transform: translateY(-1px);
}

.subtitle-style-font-option.is-selected {
  border-color: #c084fc;
  background: linear-gradient(145deg, rgba(168, 85, 247, 0.26), rgba(217, 70, 239, 0.14));
  box-shadow: 0 0 0 1px rgba(192, 132, 252, 0.32);
}

.subtitle-style-font-option:focus-visible {
  outline: 2px solid #c084fc;
  outline-offset: 2px;
}

.subtitle-style-font-sample {
  display: grid;
  min-height: 52px;
  place-items: center;
  border-radius: 7px;
  background: #08070c;
  color: #fff;
  font-size: 1.3rem;
  font-weight: 700;
  line-height: 1;
  -webkit-text-stroke: 1px #000;
  text-shadow: 1px 1px 0 #000;
}

.subtitle-style-font-name {
  overflow: hidden;
  color: var(--clipr-text-secondary);
  font-size: 0.64rem;
  font-weight: 600;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.subtitle-style-font-option.is-selected .subtitle-style-font-name {
  color: #f3e8ff;
}

.subtitle-style-field .form-control {
  min-height: 40px;
  border: 1px solid rgba(148, 163, 184, 0.26);
  border-radius: 9px;
  background-color: #211b2e;
  color: #f8fafc;
  font-size: 0.86rem;
  font-weight: 500;
}

.subtitle-style-field .form-control:focus {
  border-color: rgba(192, 132, 252, 0.72);
  box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.16);
  outline: none;
}

.subtitle-style-field select.form-control option {
  background: #211b2e;
  color: #f8fafc;
}

.subtitle-style-color-control {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-height: 40px;
  padding: 0.35rem 0.5rem;
  border: 1px solid rgba(192, 132, 252, 0.35);
  border-radius: 9px;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.14), #211b2e 52%);
  color: #f8fafc;
  font-family: monospace;
  font-size: 0.86rem;
  letter-spacing: 0.04em;
}

.subtitle-style-color-picker {
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  overflow: hidden;
  background: transparent;
  cursor: pointer;
}

.subtitle-style-color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.subtitle-style-color-picker::-webkit-color-swatch {
  border: 0;
  border-radius: 50%;
}

.subtitle-style-color-picker::-moz-color-swatch {
  border: 0;
  border-radius: 50%;
}

.subtitle-style-preview {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.35em;
  min-height: 104px;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  background: #08070c;
  color: #fff;
  font-size: clamp(1.35rem, 7vw, 2rem);
  font-weight: 700;
  line-height: 1.15;
  padding: 1rem;
  text-align: center;
  text-transform: uppercase;
  -webkit-text-stroke: 1px #000;
  text-shadow: 1px 1px 0 #000;
}

.subtitle-style-preview-active {
  color: var(--subtitle-preview-active-color);
}

.subtitle-style-preview > span {
  transform: scale(var(--subtitle-preview-font-scale));
}

@font-face {
  font-family: "Clipr Subtitle DejaVu Sans";
  src: url("../assets/fonts/subtitle-preview/DejaVuSans-Bold.ttf") format("truetype");
  font-weight: 700;
  font-display: block;
}

@font-face {
  font-family: "Clipr Subtitle DejaVu Sans Condensed";
  src: url("../assets/fonts/subtitle-preview/DejaVuSansCondensed-Bold.ttf") format("truetype");
  font-weight: 700;
  font-display: block;
}

@font-face {
  font-family: "Clipr Subtitle Liberation Sans";
  src: url("../assets/fonts/subtitle-preview/LiberationSans-Bold.ttf") format("truetype");
  font-weight: 700;
  font-display: block;
}

@font-face {
  font-family: "Anton";
  src: url("../assets/fonts/subtitle-preview/Anton-Regular.ttf") format("truetype");
  font-weight: 700;
  font-display: block;
}

@font-face {
  font-family: "Archivo Black";
  src: url("../assets/fonts/subtitle-preview/ArchivoBlack-Regular.ttf") format("truetype");
  font-weight: 700;
  font-display: block;
}

@font-face {
  font-family: "Bebas Neue";
  src: url("../assets/fonts/subtitle-preview/BebasNeue-Regular.ttf") format("truetype");
  font-weight: 700;
  font-display: block;
}

@font-face {
  font-family: "Bricolage Grotesque";
  src: url("../assets/fonts/subtitle-preview/BricolageGrotesque.ttf") format("truetype");
  font-weight: 100 900;
  font-display: block;
}

@font-face {
  font-family: "League Spartan";
  src: url("../assets/fonts/subtitle-preview/LeagueSpartan.ttf") format("truetype");
  font-weight: 100 900;
  font-display: block;
}

@font-face {
  font-family: "Oswald";
  src: url("../assets/fonts/subtitle-preview/Oswald.ttf") format("truetype");
  font-weight: 200 700;
  font-display: block;
}

.subtitle-style-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
  margin-top: 1.1rem;
}

/* ===== Responsive ===== */

@media (max-width: 991px) {
  .user-profile-page {
    --page-overlay-offset-y: 10px;
    --page-overlay-offset-x: 18px;
    margin-left: -15px;
    margin-right: -15px;
    padding: 0 1rem 1.25rem;
  }

  .user-profile-shell {
    width: 100%;
    padding-top: 0;
  }
}

@media (max-width: 575px) {
  .user-profile-page {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .user-profile-divider {
    margin: 0 1rem;
  }

  .profile-preferences-row {
    grid-template-columns: 1fr;
  }

  .preference-cell {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .preference-cell--left {
    border-right: none;
    border-bottom: 1px solid rgba(148, 163, 184, 0.09);
  }

  .profile-language-row :deep(.profile-language-select) {
    width: 6.75rem;
  }

  .admin-settings-header {
    padding: 0.95rem 1rem 0.2rem;
  }

  .user-profile-card--admin :deep(.members-settings-card-header) {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .user-profile-card--admin :deep(.members-settings-card-body) {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .admin-only-badge {
    font-size: 0.62rem;
  }
}
</style>
